import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { FormSection } from '@/common/form/FormSection';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

export const TestResList = ({ testRunId }) => {
  // const augRowsReq = useCallback((rowsReq) => {
  //   rowsReq.expr = ExprUtils.whereParams('testRunId', testRunId);
  //   rowsReq.sortModel = [{ colId: 'id', sort: 'asc' }];
  // }, [testRunId])

  // const augFieldDefs = (fieldToFieldDef) => {
  //   const instructions = fieldToFieldDef['instructions'];
  //   if (instructions) {
  //     instructions.cellStyle = {
  //       whiteSpace: 'normal',
  //       wordBreak: 'break-word',
  //       maxWidth: 700, // px, adjust as needed
  //       overflowWrap: 'break-word',
  //     };
  //     instructions.autoHeight = true;
  //     instructions.maxWidth = 700; // AG Grid property, for column resizing
  //   }
  // };

  // return (
  //   <EntityList
  //     entity="TEST_RES"
  //     augRowsReq={augRowsReq}
  //     augFieldDefs={augFieldDefs}
  //   />
  // );

  const rsrc = useRsrc();
  const [testRess, setTestRess] = useState([]);
  const [activeImage, setActiveImage] = useState(null);

  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } =
    useEntityChange();
  useEffect(() => {
    subscrToEntityChange('ac', 'TEST_RES');
    return () => {
      unsubscrToEntityChange('ac', 'TEST_RES');
    };
  }, []);

  useEffect(() => {
    rsrc.data('ac', 'TEST_RES_DATA_PLUGIN', { testRunId }).then((res) => {
      setTestRess(res);
    });
  }, [testRunId, refreshTrigger]);

  const sections = useMemo(() => {
    return testRess.reduce((acc, row) => {
      const section = row.section || 'General';
      if (!acc[section]) acc[section] = [];
      acc[section].push(row);
      return acc;
    }, {});
  }, [testRess]);

  const imageFromBase64 = (base64) => {
    if (!base64) return null;
    return `data:image/png;base64,${base64}`;
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} overflow="auto">
      {Object.entries(sections).map(([section, sectionRows]) => (
        <FormSection key={section} title={section}>
          <Table
            size="small"
            sx={(theme) => ({ borderColor: theme.palette.divider })}
          >
            <TableHead>
              <TableRow>
                <TableCell>Requirement</TableCell>
                <TableCell>Instructions</TableCell>
                <TableCell>Expected Result</TableCell>
                <TableCell>Screenshot</TableCell>
                <TableCell>Pass/Fail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionRows.map((testRes, index) => {
                const imageSrc = imageFromBase64(testRes.screenshot);
                const isPass = testRes.status === 'SUCCESS';

                return (
                  <TableRow key={`${section}-${index}`}>
                    <TableCell>{testRes.rqmtCode || ''}</TableCell>
                    <TableCell
                      sx={{
                        fontSize: 18,
                        maxWidth: 320,
                        p: 0.5,
                      }}
                    >
                      {renderWithNewlineSpacing(testRes.instructions)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 18,
                        maxWidth: 320,
                        p: 0.5,
                      }}
                    >
                      {renderWithNewlineSpacing(testRes.expectedRes)}
                    </TableCell>
                    <TableCell>
                      {imageSrc ?
                        <Box
                          component="img"
                          src={imageSrc}
                          alt="Screenshot"
                          onClick={() => setActiveImage(imageSrc)}
                          sx={(theme) => ({
                            width: 700,
                            height: 400,
                            objectFit: 'cover',
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                          })}
                        />
                      : <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <FontAwesomeIcon
                        size="2x"
                        icon={isPass ? fas.faCheck : fas.faBan}
                        color={isPass ? 'green' : 'red'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </FormSection>
      ))}

      <Dialog
        open={Boolean(activeImage)}
        onClose={() => setActiveImage(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          {activeImage && (
            <Box
              component="img"
              src={activeImage}
              alt="Screenshot"
              sx={{ width: '100%', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Helper to render text with extra spacing for explicit newlines
const renderWithNewlineSpacing = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  if (lines.length === 1) {
    // No explicit newlines, render normally
    return (
      <span
        style={{
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
          lineHeight: 1.3, // normal line height
        }}
      >
        {text}
      </span>
    );
  }
  // Explicit newlines: render each line with extra spacing
  return (
    <span>
      {lines.map((line, idx) => (
        <span
          key={idx}
          style={{
            display: 'block',
            marginBottom: idx < lines.length - 1 ? 8 : 0, // 8px extra space between lines
            lineHeight: 1.75, // slightly larger
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
          }}
        >
          {line}
        </span>
      ))}
    </span>
  );
};
